import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

/**
 * Wizard-only owner schema:
 * - Only `name` is required
 * - Other fields validate format only when filled
 *
 * IMPORTANT: Do not replace the shared `ownerSchema` in `src/lib/validations/owner.ts`
 * because Create/Edit Owner screens may rely on its stricter requirements.
 */
export const ownerWizardSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, t("nameMin"))
    .max(100, t("nameTooLong")),
  gender_id: z.string().uuid().optional().or(z.literal("")),
  document_type_id: z.string().uuid().optional().or(z.literal("")),
  document: z.string().trim().optional().or(z.literal("")),
  birth_date: z
    .string()
    .optional()
    .or(z.literal(""))
      .refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        return date <= new Date();
      },
      { message: t("birthDateNotFuture") },
    ),
  profession: z.string().trim().optional().or(z.literal("")),
  notes: z.string().trim().optional().or(z.literal("")),
  marketing_consent: z.boolean().default(true),
  lead_source_id: z.string().uuid().optional().or(z.literal("")),
  phones: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        phone_number: z
          .string()
          .trim()
          .max(15, t("phoneTooLong"))
          // Accepts empty, spaces, common symbols. (Empty string is allowed by the regex.)
          .regex(/^[\d\s()+-]*$/, t("phoneInvalid"))
          .optional()
          .or(z.literal("")),
        is_whatsapp: z.boolean().default(false),
        is_primary: z.boolean().default(false),
      }),
    )
    .min(0),
  emails: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        email: z.union([
          z.literal(""),
          z.string().trim().email(t("emailInvalid")).max(255, t("emailTooLong")),
        ]),
        is_primary: z.boolean().default(false),
      }),
    )
    .min(0),
  addresses: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        zip_code: z.string().trim().optional().or(z.literal("")),
        street: z.string().trim().optional().or(z.literal("")),
        number: z.string().trim().optional().or(z.literal("")),
        complement: z.string().trim().optional().or(z.literal("")),
        neighborhood: z.string().trim().optional().or(z.literal("")),
        city_id: z.string().uuid().optional().or(z.literal("")),
        is_primary: z.boolean().default(false),
      }),
    )
    .optional(),
});

export type OwnerWizardInput = z.infer<typeof ownerWizardSchema>;
